<template>
  <div :class="classes">
    <span
      v-if="modelValue !== undefined"
      class="close-btn"
      @click="clearValue" @keyDown="clearValue">
      <dx-icon :icon="['fas', 'xmark']" />
    </span>
    <input
      :class="{ opened: showOptions, 'options-search': optionsSearch }"
      :aria-label="label"
      :value="value"
      @input="searchOptions"
      @focus="setShowOptions(true)"
      @blur="setShowOptions(false);validateSelect()"
      placeholder=" "
      :disabled="disabled"
      :readonly="optionsSearch === undefined"
      />
    <select
      :value="modelValue"
      :aria-label="label"
      :required="required"
      :disabled="disabled">
      <option :value="modelValue">
        {{ modelValue }}
      </option>
    </select>
    <ul class="options" v-if="showOptions && optionsData.length > 0">
      <li class="option"
        v-for="option in optionsData"
        :key="option.label"
        @click="updateModelValue(option.value)"
        @keyDown="updateModelValue(option.value)"
        >
        {{ option.label }}
      </li>
    </ul>
    <span class="floating-label">
      {{ label }}
    </span>
    <p v-if="errorMessageActual">{{errorMessageActual}}</p>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

library.add(faXmark);

export default {
  name: 'dx-select',
  props: {
    errorMessage: String,
    label: String,
    modelValue: [String, Object],
    required: Boolean,
    disabled: Boolean,
    optionsSearch: Function,
    options: Array,
    labelConvert: {
      type: Function,
      default: (el) => el.name,
    },
  },
  watch: {
    modelValue(newValue) {
      this.value = newValue?.name || newValue || '';
      this.validateSelect();
    },
    options(newValue) {
      this.optionsData = newValue || [];
    },
    errorMessage(newValue) {
      this.errorMessageActual = newValue;
    },
  },
  data() {
    return {
      errorMessageActual: this.errorMessage,
      showOptions: false,
      optionsData: this.options || [],
      value: this.modelValue?.name || this.modelValue || '',
    };
  },
  computed: {
    classes() {
      return {
        'select-error': this.errorMessageActual !== undefined,
      };
    },
  },
  mounted() {
    this.addValidationOnForm();
  },
  methods: {
    addValidationOnForm() {
      const parent = this.$parent;
      if (parent?.$?.type?.name === 'dx-form') {
        parent.validations.push(this.validateSelect);
      }
    },
    async validateSelect() {
      this.errorMessageActual = undefined;

      if (!this.disabled) {
        if (this.required && this.value === '') {
          this.errorMessageActual = 'Este campo é obrigatório';
        }
      }

      return this.errorMessageActual === undefined;
    },
    setShowOptions(value) {
      if (value) {
        this.showOptions = value;
        if (this.optionsSearch) {
          this.searchOptions({ target: { value: this.value } });
        }
      } else {
        setTimeout(() => {
          this.showOptions = value;
          this.value = this.modelValue?.name || this.modelValue || '';
          if (this.optionsSearch) {
            this.optionsData = [];
          }
        }, 200);
      }
    },
    clearValue() {
      this.value = '';
      this.updateModelValue(undefined);
    },
    searchOptions(event) {
      this.value = event.target.value;
      clearTimeout(this.searchOptionsTimeout);

      this.searchOptionsTimeout = setTimeout(() => {
        this.optionsSearch(event.target.value).then((data) => {
          this.optionsData = (data?.results || data || [])
            .map((el) => ({ label: this.labelConvert(el), value: el }));
        });
      }, 500);
    },
    updateModelValue(value) {
      this.$emit('update:modelValue', value);
    },
  },
};
</script>

<style scoped lang="scss">
  div {
    position: relative;
    padding: var(--dx-space-size--xs);
  }
  input {
    width: 100%;
    min-height: 40px;
    padding: var(--dx-space-size--xs) var(--dx-space-size--sm);
    border: 2px solid var(--dx-color--grey-semi-light);
    border-radius: 3px;

    font-size: var(--dx-text-size--md);
    text-decoration: none;
    white-space: nowrap;
    vertical-align: middle;
    user-select: none;
    line-height: inherit;
    outline: none !important;

    -webkit-appearance: none;
    -moz-appearance: none;
    text-indent: 1px;
    text-overflow: '';

    &:not(.options-search) {
      cursor: pointer;
      color: fieldtext;
      background-color: field;
      opacity: 1;
    }

    &.opened, &:focus, &:hover {
      border-color: var(--dx-color--grey);
    }
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
    }

    option:checked{
      color: rgb(246, 250, 0) !important;
      background-color: rgb(5, 26, 1) !important;
    }

    &:focus ~ .floating-label,
    &:not(:placeholder-shown) ~ .floating-label,
    &.opened ~ .floating-label,
    &:-webkit-autofill ~ .floating-label {
      top: 2px;
      bottom: 10px;
      left: 20px;
      font-size: 11px !important;
      opacity: 1;
      height: 15px;
      background: #fff;
      color: #000;
    }
    &:-webkit-autofill ~ .floating-label {
      transition: 0.0s ease all;
    }
    &:disabled {
      background-color: var(--dx-color--grey-light);
      color:  var(--dx-color--grey-dark);
      opacity: 0.8;
      cursor: not-allowed;
    }
  }
  .floating-label {
    position: absolute;
    pointer-events: none;
    left: var(--dx-space-size--sm);
    top: calc(var(--dx-space-size--sm) + 3px);
    padding: 1px 6px;
    transition: 0.2s ease all;
    color: var(--dx-color--grey-dark);
    border-radius: var(--dx-space-size--md);

    max-width: calc(100% - var(--dx-space-size--lg));
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  p {
    margin: 0px var(--dx-space-size--xs) calc(var(--dx-space-size--xs) * -1);

    font-size: var(--dx-text-size--sm);
    text-align: left !important;
  }
  .select-error{
    .options-search {
      border-color: var(--dx-color--red) !important;
    }
    .options {
      border-top-color: var(--dx-color--red-light) !important;
      border-bottom-color: var(--dx-color--red) !important;
      border-left-color: var(--dx-color--red) !important;
      border-right-color: var(--dx-color--red) !important;
    }
    p {
      color: var(--dx-color--red) !important;
    }
  }
  .close-btn {
    cursor: pointer;
    position: absolute;
    right: var(--dx-space-size--xs);
    padding: 12px var(--dx-space-size--xs);
    color: var(--dx-color--grey);
    font-size: var(--dx-text-size--sm);

    & ~ input {
      padding-right: var(--dx-space-size--md);
    }

    &:hover {
      opacity: 0.8;
      color: var(--dx-color--grey-semi-light);
    }
  }
  .loading * {
    pointer-events: none!important;
    cursor: wait !important;
    opacity: 0.8 !important;
  }
  .options {
    margin-top: -2px;
    position: absolute;
    background: #fff;
    left: var(--dx-space-size--xs);
    right: var(--dx-space-size--xs);
    padding: 0;
    border-top: 2px solid var(--dx-color--grey-light);
    border-bottom: 2px solid var(--dx-color--grey);
    border-left: 2px solid var(--dx-color--grey);
    border-right: 2px solid var(--dx-color--grey);
    border-radius: 0 0 3px 3px;
    z-index: 10;
    list-style-type: none;

    .option {
      cursor: pointer;
      padding: var(--dx-space-size--xs);

      &:hover {
        opacity: 0.8 !important;
        background: var(--dx-color--grey-semi-light);
      }
    }
  }
  select {
    position: absolute;
    left: 0;
    right: 0;
    bottom: var(--dx-space-size--xs);
    opacity: 0;
    z-index: -10;
  }
</style>
